import "@astryxdesign/core/reset.css";
import "@astryxdesign/core/astryx.css";
import "./psychometrics-theme.css";
import "./psychometrics.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Legend, Tooltip } from "chart.js";
import { AppShell } from "@astryxdesign/core/AppShell";
import { Avatar } from "@astryxdesign/core/Avatar";
import { Badge } from "@astryxdesign/core/Badge";
import { Banner } from "@astryxdesign/core/Banner";
import { Button } from "@astryxdesign/core/Button";
import { Card } from "@astryxdesign/core/Card";
import { Grid } from "@astryxdesign/core/Grid";
import { Heading, Text } from "@astryxdesign/core/Text";
import { HStack } from "@astryxdesign/core/HStack";
import { ProgressBar } from "@astryxdesign/core/ProgressBar";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Section } from "@astryxdesign/core/Section";
import { SelectableCard } from "@astryxdesign/core/SelectableCard";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { SegmentedControl, SegmentedControlItem } from "@astryxdesign/core/SegmentedControl";
import { Selector } from "@astryxdesign/core/Selector";
import { TextInput } from "@astryxdesign/core/TextInput";
import { TopNav, TopNavHeading, TopNavItem } from "@astryxdesign/core/TopNav";
import { VStack } from "@astryxdesign/core/VStack";
import { Theme, useTheme } from "@astryxdesign/core/theme";
import { InternationalizationProvider } from "@astryxdesign/core/i18n";
import type { TestDefinition, User } from "./types";
import { psychometricsMatchaTheme } from "./psychometrics-matcha";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Legend, Tooltip);

type Session = { user: User | null; csrfToken?: string };
type Attempt = {
  id: number;
  test_id: string;
  status: "in_progress" | "submitted";
  answered_count: number;
  started_at: string;
  submitted_at?: string;
};
type ResultSummary = {
  id: number;
  test_id: string;
  test_title: string;
  test_short_title: string;
  user_id: number;
  display_name: string;
  submitted_at: string;
  test_version: string;
};

let csrfToken = "";

const astryxDanish = {
  "@astryx.mobileNav.toggle.open": {
    defaultMessage: "Åbn navigation",
    description: "Skærmlæsertekst til knappen, der åbner mobilnavigationen.",
  },
  "@astryx.mobileNav.closeNavigation": {
    defaultMessage: "Luk navigation",
    description: "Skærmlæsertekst til knappen, der lukker mobilnavigationen.",
  },
};

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionError, setSessionError] = useState("");
  const [route, setRoute] = useState(parseRoute());

  useEffect(() => {
    api<Session>("/api/me").then(data => {
      csrfToken = data.csrfToken || "";
      setSession(data);
    }).catch(error => setSessionError(message(error)));
    const onHash = () => setRoute(parseRoute());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  if (sessionError) {
    return (
      <AppShell contentPadding={4} height="fill" variant="wash">
        <VStack minHeight="80dvh" hAlign="center" vAlign="center" gap={3}>
          <Banner
            status="error"
            title="Appen kunne ikke indlæses"
            description={sessionError}
          />
          <Button label="Prøv igen" variant="secondary" onClick={() => location.reload()} />
        </VStack>
      </AppShell>
    );
  }
  if (!session) return <LoadingPage />;
  if (!session.user) return <Login onLogin={data => { csrfToken = data.csrfToken; setSession(data); }} />;

  const user = session.user;
  const nav = (
    <TopNav
      heading={<TopNavHeading heading="Psychometrics" subheading="Åbne, reproducerbare målinger" headingHref="#/" />}
      startContent={
        <HStack gap={1}>
          <TopNavItem label="Overblik" href="#/" isSelected={route.name === "dashboard"} />
          <TopNavItem label="Resultater" href="#/results" isSelected={route.name === "results"} />
          {user.role === "admin" && <TopNavItem label="Brugere" href="#/admin" isSelected={route.name === "admin"} />}
          <TopNavItem label="Konto" href="#/account" isSelected={route.name === "account"} />
        </HStack>
      }
      endContent={<Button label={`Log ud · ${user.display_name}`} variant="ghost" clickAction={async () => {
        await api("/api/logout", { method: "POST" });
        csrfToken = "";
        setSession({ user: null });
        location.hash = "";
      }} />}
    />
  );

  return (
    <AppShell topNav={nav} contentPadding={0} height="auto" variant="surface">
      {user.must_change_password === 1 && route.name !== "account" && (
        <Banner status="warning" title="Skift din midlertidige adgangskode" description="Din administrator har sat en midlertidig adgangskode. Skift den under Konto." />
      )}
      {route.name === "dashboard" && <Dashboard user={user} />}
      {route.name === "test" && <TestView attemptId={route.id} />}
      {route.name === "results" && <Results />}
      {route.name === "result" && <ResultView resultId={route.id} />}
      {route.name === "compare" && <CompareView leftId={route.leftId} rightId={route.rightId} />}
      {route.name === "admin" && user.role === "admin" && <Admin />}
      {route.name === "account" && <Account user={user} onChanged={() => setSession({ ...session, user: { ...user, must_change_password: 0 } })} />}
    </AppShell>
  );
}

function LoadingPage() {
  return (
    <AppShell contentPadding={0} height="fill" variant="wash">
      <VStack gap={5} maxWidth={1120} className="page-frame loading-page">
        <Skeleton width="32%" height={18} />
        <Skeleton width="58%" height={42} />
        <Grid columns={{ minWidth: 300, max: 2 }} gap={4}>
          <Skeleton height={260} radius={4} />
          <Skeleton height={260} radius={4} index={1} />
        </Grid>
      </VStack>
    </AppShell>
  );
}

function Login({ onLogin }: { onLogin: (session: Session & { user: User; csrfToken: string }) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  return (
    <AppShell contentPadding={4} height="fill" variant="wash">
      <VStack minHeight="80dvh" hAlign="center" vAlign="center" className="login-stage">
        <Card width="100%" maxWidth={440} padding={6} className="login-card">
          <VStack gap={5}>
            <VStack gap={2}>
              <Text type="supporting" weight="semibold" color="accent" className="eyebrow">Privat målerum</Text>
              <Heading level={1} type="display-3">Forstå jeres dynamik.</Heading>
              <Text color="secondary">Åbne, reproducerbare personlighedsmålinger til bedre samarbejde.</Text>
            </VStack>
            {error && <Banner status="error" title="Login mislykkedes" description={error} />}
            <TextInput label="Brugernavn" value={username} onChange={setUsername} aria-required="true" hasAutoFocus />
            <TextInput label="Adgangskode" type="password" value={password} onChange={setPassword} aria-required="true" />
            <Button label="Log ind" variant="primary" width="100%" clickAction={async () => {
              setError("");
              try {
                const data = await api<Session & { user: User; csrfToken: string }>("/api/login", {
                  method: "POST",
                  body: JSON.stringify({ username, password })
                });
                onLogin(data);
              } catch (error) {
                setError(message(error));
              }
            }} />
          </VStack>
        </Card>
      </VStack>
    </AppShell>
  );
}

function Dashboard({ user }: { user: User }) {
  const [definitions, setDefinitions] = useState<TestDefinition[]>([]);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api<{ tests: TestDefinition[] }>("/api/tests"), api<{ attempts: Attempt[] }>("/api/attempts")])
      .then(([catalog, data]) => { setDefinitions(catalog.tests); setAttempts(data.attempts); })
      .catch(error => setError(message(error)));
  }, []);

  async function openAssessment(definition: TestDefinition) {
    setError("");
    try {
      const data = await api<{ attemptId: number }>("/api/attempts", {
        method: "POST",
        body: JSON.stringify({ testId: definition.id }),
      });
      location.hash = `#/test/${data.attemptId}`;
    } catch (error) {
      setError(message(error));
    }
  }

  return (
    <VStack gap={8} maxWidth={1120} className="page-frame">
      <VStack gap={2} className="page-intro">
        <Text type="supporting" weight="semibold" color="accent" className="eyebrow">Dit målerum</Text>
        <Heading level={1} type="display-2">Godt at se dig, {user.display_name}</Heading>
        <Text type="large" color="secondary">Se personlighed som mønstre og nuancer — aldrig som faste kasser.</Text>
      </VStack>
      {error && <Banner status="error" title="Kunne ikke hente data" description={error} />}
      {!definitions.length && !error && (
        <Grid columns={{ minWidth: 300, max: 2 }} gap={4}>
          <Skeleton height={280} radius={4} />
          <Skeleton height={280} radius={4} index={1} />
        </Grid>
      )}
      <Grid columns={{ minWidth: 300, max: 2 }} gap={4}>
        {definitions.map((definition, index) => {
          const active = attempts.find(attempt => attempt.status === "in_progress" && attempt.test_id === definition.id);
          const completed = attempts.filter(attempt => attempt.status === "submitted" && attempt.test_id === definition.id);
          const latestCompleted = completed[0]?.submitted_at;
          const isDemo = definition.itemCount === 10;
          const category = isDemo
            ? "Systemtest"
            : definition.id.includes("ipc")
              ? "Samarbejde"
              : definition.id.includes("via")
                ? "Karakterstyrker"
                : "Personlighed";
          return (
            <Card key={definition.id} padding={6} variant={isDemo ? "muted" : "default"} className={index === 0 ? "assessment-primary" : "assessment-secondary"}>
              <VStack gap={5}>
                <VStack gap={2}>
                  <HStack hAlign="between" vAlign="center" gap={3} wrap="wrap">
                    <Text type="supporting" weight="semibold" color={isDemo ? "secondary" : "accent"} className="eyebrow">
                      {category}
                    </Text>
                    {active && <Badge label="I gang" variant="green" />}
                  </HStack>
                  <Heading level={2} type="display-3">{definition.title}</Heading>
                  <Text color="secondary">{definition.context.purpose}</Text>
                </VStack>
                <Grid columns={{ minWidth: 110, max: 3 }} gap={3}>
                  <VStack gap={0.5}>
                    <Text type="supporting" color="secondary">Spørgsmål</Text>
                    <Text type="large" weight="semibold" className="metric-number">{definition.itemCount}</Text>
                  </VStack>
                  <VStack gap={0.5}>
                    <Text type="supporting" color="secondary">Tidsforbrug</Text>
                    <Text type="large" weight="semibold" className="metric-number">ca. {definition.estimatedMinutes} min.</Text>
                  </VStack>
                  <VStack gap={0.5}>
                    <Text type="supporting" color="secondary">{isDemo ? "Formål" : "Skalaer"}</Text>
                    <Text type="large" weight="semibold">{isDemo ? "Prøveflow" : `${definition.domains.length} profiler`}</Text>
                  </VStack>
                </Grid>
                <Section variant="muted" padding={3}>
                  <HStack hAlign="between" vAlign="center" gap={3} wrap="wrap">
                    <Text type="supporting" weight="semibold">
                      Taget {completed.length} {completed.length === 1 ? "gang" : "gange"}
                    </Text>
                    <Text type="supporting" color="secondary">
                      {latestCompleted ? `Senest ${formatDate(latestCompleted)}` : "Ikke gennemført endnu"}
                    </Text>
                  </HStack>
                </Section>
                <Button
                  label={active ? "Fortsæt målingen" : isDemo ? "Prøv hele flowet" : "Start målingen"}
                  variant={isDemo ? "secondary" : "primary"}
                  width="100%"
                  onClick={() => void openAssessment(definition)}
                />
                <Text type="supporting" color="secondary">{definition.context.limits[0]}</Text>
              </VStack>
            </Card>
          );
        })}
      </Grid>
      <VStack gap={4}>
        <HStack hAlign="between" vAlign="end" gap={3} wrap="wrap">
          <VStack gap={1}>
            <Heading level={2}>Dine målinger</Heading>
            <Text color="secondary">Fortsæt hvor du slap, eller genbesøg en færdig profil.</Text>
          </VStack>
          {!!attempts.length && <Text type="supporting" color="secondary">{attempts.length} i alt</Text>}
        </HStack>
        {!attempts.length && !!definitions.length && (
          <EmptyState
            title="Din første profil venter"
            description="Besvar målingen én gang, og få et detaljeret billede af fem domæner og 30 facetter."
            actions={<Button label="Start den fulde måling" variant="primary" width="100%" onClick={() => openAssessment(definitions[0])} />}
          />
        )}
        <VStack gap={2}>
          {attempts.map(attempt => (
            <Section key={attempt.id} padding={4} variant="muted">
              <Grid columns={{ minWidth: 220, max: 2 }} gap={4}>
                <VStack gap={2}>
                  <HStack gap={2} vAlign="center" wrap="wrap">
                    <Heading level={3}>{definitions.find(item => item.id === attempt.test_id)?.shortTitle || "Personlighedsmåling"}</Heading>
                    <Badge label={attempt.status === "submitted" ? "Afsluttet" : "I gang"} variant={attempt.status === "submitted" ? "success" : "neutral"} />
                  </HStack>
                  <Text type="supporting" color="secondary">
                    {attempt.status === "submitted" && attempt.submitted_at ? `Afsluttet ${formatDate(attempt.submitted_at)}` : `Startet ${formatDate(attempt.started_at)}`}
                  </Text>
                </VStack>
                <VStack gap={3}>
                  <ProgressBar
                    label="Besvarelsesgrad"
                    value={attempt.status === "submitted" ? (definitions.find(item => item.id === attempt.test_id)?.itemCount || 120) : attempt.answered_count}
                    max={definitions.find(item => item.id === attempt.test_id)?.itemCount || 120}
                    hasValueLabel
                    variant={attempt.status === "submitted" ? "success" : "accent"}
                  />
                  <Button
                    label={attempt.status === "submitted" ? "Se resultat" : "Fortsæt målingen"}
                    variant="secondary"
                    onClick={() => location.hash = attempt.status === "submitted" ? `#/result/${attempt.id}` : `#/test/${attempt.id}`}
                  />
                </VStack>
              </Grid>
            </Section>
          ))}
        </VStack>
      </VStack>
    </VStack>
  );
}

function TestView({ attemptId }: { attemptId: number }) {
  const [definition, setDefinition] = useState<TestDefinition | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const itemStartedAt = useRef(Date.now());

  useEffect(() => {
    api<{ attempt: Attempt; responses: Array<{ item_id: number; value: number }> }>(`/api/attempts/${attemptId}`)
      .then(async attempt => {
        const test = await api<TestDefinition>(`/api/test?testId=${encodeURIComponent(attempt.attempt.test_id)}`);
        setDefinition(test);
        const existing = Object.fromEntries(attempt.responses.map(response => [response.item_id, response.value]));
        setAnswers(existing);
        const firstMissing = test.items.findIndex(item => !existing[item.id]);
        const testPageSize = test.responseMode === "matrix-multiselect" ? 1 : 10;
        if (firstMissing >= 0) setPage(Math.floor(firstMissing / testPageSize));
      })
      .catch(error => setError(message(error)));
  }, [attemptId]);

  if (!definition && error) {
    return (
      <VStack gap={4} maxWidth={900} className="page-frame">
        <Banner status="error" title="Målingen kunne ikke åbnes" description={error} />
        <Button label="Tilbage til overblik" variant="secondary" onClick={() => location.hash = "#/"} />
      </VStack>
    );
  }
  if (!definition) {
    return (
      <VStack gap={5} maxWidth={900} className="page-frame loading-page">
        <Skeleton width="45%" height={34} />
        <Skeleton width="100%" height={52} radius={3} />
        <Skeleton width="100%" height={230} radius={4} index={1} />
        <Skeleton width="100%" height={230} radius={4} index={2} />
      </VStack>
    );
  }
  const pageSize = definition.responseMode === "matrix-multiselect" ? 1 : 10;
  const items = definition.items.slice(page * pageSize, (page + 1) * pageSize);
  const pageCount = Math.ceil(definition.items.length / pageSize);
  const answered = Object.keys(answers).length;
  const pageAnswered = items.filter(item => answers[item.id]).length;
  const isPageComplete = pageAnswered === items.length;
  const options = definition.context.responseScale.map(option => ({ value: String(option.value), label: `${option.value} · ${option.label}` }));

  async function save() {
    setSaveState("saving");
    const responses = Object.entries(answers).map(([itemId, value]) => ({ itemId: Number(itemId), value }));
    await api(`/api/attempts/${attemptId}`, { method: "PUT", body: JSON.stringify({ responses }) });
    setSaveState("saved");
  }

  async function chooseAnswer(itemId: number, value: number, responseTimeMs?: number) {
    setAnswers(current => {
      const updated = { ...current };
      if (value === 0) delete updated[itemId];
      else updated[itemId] = value;
      return updated;
    });
    setSaveState("saving");
    setError("");
    try {
      await api(`/api/attempts/${attemptId}`, {
        method: "PUT",
        body: JSON.stringify({ responses: [{ itemId, value, responseTimeMs }] }),
      });
      setSaveState("saved");
    } catch (error) {
      setSaveState("idle");
      setError(message(error));
    }
  }

  function movePage(nextPage: number) {
    itemStartedAt.current = Date.now();
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function toggleMatrixElement(itemId: number, optionIndex: number) {
    const current = answers[itemId] || 0;
    const next = current ^ (1 << optionIndex);
    void chooseAnswer(itemId, next, Date.now() - itemStartedAt.current);
  }

  return (
    <VStack gap={6} maxWidth={900} className={`page-frame test-page${definition.responseMode === "matrix-multiselect" ? " matrix-test-page" : ""}`}>
      <VStack gap={2}>
        <HStack hAlign="between" vAlign="center" wrap="wrap" gap={2}>
          <VStack gap={0.5}>
            <Heading level={1}>{definition.shortTitle}</Heading>
            <Text color="secondary">Side {page + 1} af {pageCount} · {pageAnswered} af {items.length} besvaret på siden</Text>
          </VStack>
          <VStack gap={1} hAlign="end">
            <Button label="Gem og gå til overblik" variant="ghost" clickAction={async () => { await save(); location.hash = "#/"; }} />
            <Text type="supporting" color="secondary" role="status">
              {saveState === "saving" ? "Gemmer…" : saveState === "saved" ? "Alle svar er gemt" : "Svar gemmes automatisk"}
            </Text>
          </VStack>
        </HStack>
        <ProgressBar label="Samlet besvarelse" value={answered} max={definition.itemCount} hasValueLabel />
      </VStack>
      {error && <Banner status="error" title="Der opstod en fejl" description={error} />}
      {definition.responseMode !== "matrix-multiselect" && (
        <Section variant="muted" padding={4}>
          <VStack gap={1}>
            <Heading level={3}>Svar som du generelt er nu</Heading>
            <Text color="secondary">{definition.context.instructions || "Vælg hvor præcist hvert udsagn beskriver dig. Der findes ikke rigtige eller forkerte svar."}</Text>
          </VStack>
        </Section>
      )}
      <VStack gap={5}>
        {items.map(item => definition.responseMode === "matrix-multiselect" ? (
          <Card key={item.id} padding={5} className="question-card matrix-question">
            <VStack gap={5}>
              <HStack hAlign="between" vAlign="start" gap={3}>
                <VStack gap={1}>
                  <Heading level={2}>Opgave {item.id} af {definition.itemCount}</Heading>
                  <Text color="secondary">{item.text}</Text>
                </VStack>
                <Badge label={String(item.id).padStart(2, "0")} variant="neutral" />
              </HStack>
              <Grid columns={{ minWidth: 280, max: 2 }} gap={4} className="matrix-workspace">
                <Section variant="muted" padding={3} className="matrix-figure">
                  <VStack gap={1} hAlign="center">
                    <Text type="supporting" weight="semibold">Oprindeligt mønster</Text>
                    <img src={item.mediaUrl} alt={`Figurmatrix til opgave ${item.id}; feltet nederst til højre mangler`} />
                  </VStack>
                </Section>
                <VStack gap={2} className="matrix-choices">
                  <Text weight="semibold">Vælg alle elementer, der skal indgå</Text>
                  <Text type="supporting" color="secondary">Der kan være mere end ét. Valgte felter får en tydelig grøn ramme.</Text>
                  <Grid columns={{ minWidth: 48, max: 5 }} gap={2} className="matrix-options">
                  {Array.from({ length: 20 }, (_, optionIndex) => {
                    const selected = Boolean((answers[item.id] || 0) & (1 << optionIndex));
                    return (
                      <SelectableCard
                        key={optionIndex}
                        label={`Element ${optionIndex + 1}${selected ? ", valgt" : ""}`}
                        isSelected={selected}
                        variant={selected ? "green" : "default"}
                        padding={1}
                        onChange={() => toggleMatrixElement(item.id, optionIndex)}
                      >
                        <img src={`/omib-assets/options/option-${optionIndex + 1}.svg`} alt="" />
                      </SelectableCard>
                    );
                  })}
                  </Grid>
                </VStack>
              </Grid>
            </VStack>
          </Card>
        ) : (
          <Card key={item.id} padding={5} className="question-card">
            <VStack gap={4}>
              <HStack hAlign="between" vAlign="start" gap={3}>
                <Heading level={2}>{item.text}</Heading>
                <Badge label={String(item.id).padStart(3, "0")} variant="neutral" />
              </HStack>
              <SegmentedControl
                label={`${item.id}. ${item.text}`}
                value={answers[item.id] ? String(answers[item.id]) : ""}
                onChange={value => void chooseAnswer(item.id, Number(value))}
                layout="fill"
                size="lg"
              >
                {options.map(option => <SegmentedControlItem key={option.value} value={option.value} label={option.value} />)}
              </SegmentedControl>
              <HStack hAlign="between" gap={3}>
                <Text type="supporting">Meget upræcist</Text>
                <Text type="supporting">Meget præcist</Text>
              </HStack>
            </VStack>
          </Card>
        ))}
      </VStack>
      <HStack hAlign="between" vAlign="center" wrap="wrap" gap={2} className="test-actions">
        <Button label="Forrige side" variant="secondary" isDisabled={page === 0} clickAction={async () => {
          await save();
          movePage(Math.max(0, page - 1));
        }} />
        {page < pageCount - 1 ? (
          <Button label={isPageComplete ? "Fortsæt til næste side" : `Besvar de sidste ${items.length - pageAnswered}`} variant="primary" isDisabled={!isPageComplete} clickAction={async () => {
            setError("");
            try {
              await save();
              movePage(Math.min(pageCount - 1, page + 1));
            } catch (error) {
              setError(message(error));
            }
          }} />
        ) : (
          <Button label="Afslut og beregn resultat" variant="primary" isDisabled={answered !== definition.itemCount} clickAction={async () => {
            setError("");
            try {
              await save();
              const data = await api<{ resultId: number }>(`/api/attempts/${attemptId}/submit`, { method: "POST" });
              location.hash = `#/result/${data.resultId}`;
            } catch (error) {
              setError(message(error));
            }
          }} />
        )}
      </HStack>
    </VStack>
  );
}

function Results() {
  const [results, setResults] = useState<ResultSummary[]>([]);
  const [left, setLeft] = useState("");
  const [right, setRight] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api<{ results: ResultSummary[] }>("/api/results")
      .then(data => setResults(data.results))
      .catch(error => setError(message(error)))
      .finally(() => setLoading(false));
  }, []);
  return (
    <VStack gap={6} maxWidth={1000} className="page-frame">
      <HStack hAlign="between" vAlign="center" wrap="wrap" gap={2}>
        <VStack gap={2}>
          <Text type="supporting" weight="semibold" color="accent" className="eyebrow">Bibliotek</Text>
          <Heading level={1} type="display-2">Resultater</Heading>
          <Text color="secondary">Afsluttede målinger med reproducerbare scorer og fuld eksport.</Text>
        </VStack>
        <Button label="Eksportér alt som JSON" variant="secondary" onClick={() => window.location.href = "/api/export-all.json"} />
      </HStack>
      {error && <Banner status="error" title="Kunne ikke hente resultater" description={error} />}
      {loading && (
        <Grid columns={{ minWidth: 280, max: 2 }} gap={3}>
          <Skeleton height={180} radius={4} />
          <Skeleton height={180} radius={4} index={1} />
        </Grid>
      )}
      {results.length >= 2 && (
        <Section variant="muted" padding={5}>
          <VStack gap={3}>
            <Heading level={2}>Sammenlign to profiler</Heading>
            <Text type="supporting">Se ligheder og forskelle side om side. Forskelle er arbejdshypoteser, ikke konfliktdomme.</Text>
            <Grid columns={{ minWidth: 240, max: 2 }} gap={3}>
              <Selector label="Profil A" options={results.map(result => ({ value: String(result.id), label: `${result.display_name} · ${result.test_id} · ${formatDate(result.submitted_at)}` }))} value={left} onChange={value => { setLeft(value); setRight(""); }} />
              <Selector label="Profil B" options={results.filter(result => !left || result.test_id === results.find(candidate => String(candidate.id) === left)?.test_id).map(result => ({ value: String(result.id), label: `${result.display_name} · ${formatDate(result.submitted_at)}` }))} value={right} onChange={setRight} />
            </Grid>
            <Button label="Åbn sammenligning" variant="primary" isDisabled={!left || !right || left === right} onClick={() => location.hash = `#/compare/${left}/${right}`} />
          </VStack>
        </Section>
      )}
      {!loading && !results.length && !error && (
        <EmptyState
          title="Ingen afsluttede målinger endnu"
          description="Når en måling er afsluttet, kan du se, sammenligne og eksportere den her."
          actions={<Button label="Gå til overblik" variant="primary" onClick={() => location.hash = "#/"} />}
        />
      )}
      <Grid columns={{ minWidth: 280, max: 2 }} gap={3}>
        {results.map(result => (
          <Card key={result.id} padding={5}>
            <VStack gap={3}>
              <Text type="supporting" weight="semibold" color={result.test_id.includes("demo") ? "secondary" : "accent"} className="eyebrow">
                {result.test_id.includes("demo") ? "Systemtest" : "Fuld profil"}
              </Text>
              <Text type="supporting" color="secondary">{result.test_title}</Text>
              <Heading level={3}>{result.display_name}</Heading>
              <Text type="supporting" color="secondary">{formatDate(result.submitted_at)}</Text>
              <Button label="Åbn resultat" variant="secondary" onClick={() => location.hash = `#/result/${result.id}`} />
            </VStack>
          </Card>
        ))}
      </Grid>
    </VStack>
  );
}

function CompareView({ leftId, rightId }: { leftId: number; rightId: number }) {
  const [profiles, setProfiles] = useState<any[] | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([api(`/api/results/${leftId}`), api(`/api/results/${rightId}`)])
      .then(setProfiles).catch(error => setError(message(error)));
  }, [leftId, rightId]);
  if (error) {
    return (
      <VStack gap={4} maxWidth={1120} className="page-frame">
        <Banner status="error" title="Kunne ikke sammenligne profilerne" description={error} />
        <Button label="Tilbage til resultater" variant="secondary" onClick={() => location.hash = "#/results"} />
      </VStack>
    );
  }
  if (!profiles) {
    return (
      <VStack gap={5} maxWidth={1120} className="page-frame loading-page">
        <Skeleton width="48%" height={42} />
        <Skeleton width="100%" height={420} radius={4} index={1} />
      </VStack>
    );
  }
  const [left, right] = profiles;
  const leftDomains = left.scores.filter((score: any) => score.kind === "domain");
  const rightDomains = right.scores.filter((score: any) => score.kind === "domain");
  const comparisonKind = left.scores.some((score: any) => score.kind === "facet") ? "facet" : "domain";
  const differences = left.scores
    .filter((score: any) => score.kind === comparisonKind)
    .map((score: any) => {
      const counterpart = right.scores.find((candidate: any) => candidate.kind === "facet" && candidate.key === score.key);
      return { ...score, left: Number(score.value), right: Number(counterpart?.value), difference: Math.abs(Number(score.value) - Number(counterpart?.value)) };
    })
    .sort((a: any, b: any) => b.difference - a.difference);
  return (
    <VStack gap={8} maxWidth={1120} className="page-frame">
      <VStack gap={2}>
        <Button label="Tilbage til resultater" variant="ghost" onClick={() => location.hash = "#/results"} />
        <Text type="supporting" weight="semibold" color="accent" className="eyebrow">Samarbejdsprofil</Text>
        <Heading level={1} type="display-2">{left.participant.displayName} + {right.participant.displayName}</Heading>
        <Text color="secondary">Samarbejdsprofil baseret på to målinger af typen {left.measurement.title}.</Text>
      </VStack>
      <Section variant="muted" padding={4}>
        <VStack gap={1}>
          <Heading level={3}>Brug forskellene som samtalestartere</Heading>
          <Text color="secondary">En afstand kan både skabe komplementaritet og friktion. Tal om konkrete arbejdssituationer, før I drager konklusioner.</Text>
        </VStack>
      </Section>
      <Card padding={5}>
        <VStack gap={3}>
          <Heading level={2}>{leftDomains.length} skalaer side om side</Heading>
          <ComparisonChart leftName={left.participant.displayName} rightName={right.participant.displayName} left={leftDomains} right={rightDomains} />
        </VStack>
      </Card>
      <VStack gap={3}>
        <Heading level={2}>Største forskelle</Heading>
        <Grid columns={{ minWidth: 300, max: 2 }} gap={3}>
          {differences.slice(0, 9).map((score: any) => (
            <Section key={score.key} variant="muted" padding={4}>
              <VStack gap={2}>
                <Heading level={3}>{score.name}</Heading>
                <Text type="supporting">{score.description}</Text>
                <Text weight="semibold">{left.participant.displayName}: {score.left}/5</Text>
                <Text weight="semibold">{right.participant.displayName}: {score.right}/5</Text>
                <Text type="supporting">Afstand: {score.difference.toFixed(2)} point</Text>
              </VStack>
            </Section>
          ))}
        </Grid>
      </VStack>
      <Section variant="muted" padding={5}>
        <VStack gap={2}>
          <Heading level={2}>Samtalespørgsmål</Heading>
          <Text>• I hvilke situationer hjælper jeres største forskelle jer med at dække hinandens blinde vinkler?</Text>
          <Text>• Hvornår risikerer de samme forskelle at blive tolket som modstand, langsomhed eller impulsivitet?</Text>
          <Text>• Hvilke beslutninger, roller og feedbackritualer bør I aftale eksplicit?</Text>
        </VStack>
      </Section>
    </VStack>
  );
}

function ResultView({ resultId }: { resultId: number }) {
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState("");
  useEffect(() => { api(`/api/results/${resultId}`).then(setResult).catch(error => setError(message(error))); }, [resultId]);
  if (error) {
    return (
      <VStack gap={4} maxWidth={1120} className="page-frame">
        <Banner status="error" title="Kunne ikke hente resultat" description={error} />
        <Button label="Tilbage til resultater" variant="secondary" onClick={() => location.hash = "#/results"} />
      </VStack>
    );
  }
  if (!result) {
    return (
      <VStack gap={5} maxWidth={1120} className="page-frame loading-page">
        <Skeleton width="38%" height={42} />
        <Skeleton width="100%" height={76} radius={3} />
        <Skeleton width="100%" height={380} radius={4} index={1} />
      </VStack>
    );
  }
  const domains = result.scores.filter((score: any) => score.kind === "domain");
  const facets = result.scores.filter((score: any) => score.kind === "facet");
  const scoreScale = result.measurement.scoreScale || { minimum: 1, maximum: 5, suffix: "/5", note: "Kontinuerlig selvrapporteringsskala." };
  const isCognitive = result.measurement.id.startsWith("omib-");
  return (
    <VStack gap={8} maxWidth={1120} className="page-frame">
      <HStack hAlign="between" vAlign="end" wrap="wrap" gap={4}>
        <VStack gap={2}>
          <Button label="Tilbage til resultater" variant="ghost" onClick={() => location.hash = "#/results"} />
          <Text type="supporting" weight="semibold" color="accent" className="eyebrow">Personlig profil</Text>
          <Heading level={1} type="display-2">{result.participant.displayName}</Heading>
          <Text color="secondary">{result.measurement.title} · afsluttet {formatDate(result.administration.submittedAt)}</Text>
        </VStack>
        <HStack gap={2} wrap="wrap">
          <Button label="Hent Markdown" variant="secondary" onClick={() => window.location.href = `/api/export/${resultId}.md`} />
          <Button label="Hent komplet JSON" variant="secondary" onClick={() => window.location.href = `/api/export/${resultId}.json`} />
        </HStack>
      </HStack>
      <Section variant="muted" padding={4}>
        <VStack gap={1}>
          <Heading level={3}>{isCognitive ? "Præstation på denne kortform — ikke et IQ-tal" : "Kontinuerlige dispositioner — ikke typer"}</Heading>
          <Text color="secondary">{isCognitive ? scoreScale.note : "En høj eller lav score er ikke automatisk god eller dårlig. Fortolk den i relation til opgave, samarbejde og situation."}</Text>
        </VStack>
      </Section>
      <Grid columns={{ minWidth: 300, max: 2 }} gap={4}>
        <Card padding={5} className="result-chart-card">
          <VStack gap={4}>
            <VStack gap={1}>
              <Heading level={2}>Profilens {domains.length} skalaer</Heading>
              <Text type="supporting" color="secondary">{isCognitive ? scoreScale.note : "Skala fra 1 til 5. Søjlerne viser placering — ikke kvalitet."}</Text>
            </VStack>
            <DomainChart scores={domains} minimum={scoreScale.minimum} maximum={scoreScale.maximum} suffix={scoreScale.suffix} />
          </VStack>
        </Card>
        <Section variant="muted" padding={5}>
          <VStack gap={4}>
            <VStack gap={1}>
              <Heading level={2}>Sådan læser du profilen</Heading>
              <Text color="secondary">{isCognitive ? "Sammenhold det sværhedsjusterede indeks med rå korrekthed og testbetingelserne." : "Start med helheden, og brug facetterne til at forstå, hvad der driver hvert domæne."}</Text>
            </VStack>
            <VStack gap={3}>
              {isCognitive ? (
                <>
                  <VStack gap={0.5}>
                    <Text weight="semibold">Instrumentindeks</Text>
                    <Text type="supporting" color="secondary">Et monotont mål baseret på opgavernes sværhed og diskrimination. Det er ikke normeret mod befolkningen.</Text>
                  </VStack>
                  <VStack gap={0.5}>
                    <Text weight="semibold">Rå korrekthed</Text>
                    <Text type="supporting" color="secondary">Procentdelen af opgaver, hvor alle nødvendige elementer — og ingen ekstra — blev valgt.</Text>
                  </VStack>
                </>
              ) : (
                <>
              <VStack gap={0.5}>
                <Text weight="semibold">1–2 · lavere udtryk</Text>
                <Text type="supporting" color="secondary">Trækket viser sig typisk mere selektivt eller situationsbestemt.</Text>
              </VStack>
              <VStack gap={0.5}>
                <Text weight="semibold">Omkring 3 · balanceret udtryk</Text>
                <Text type="supporting" color="secondary">Adfærden skifter lettere med rolle, relation og krav.</Text>
              </VStack>
                </>
              )}
              <VStack gap={0.5}>
                <Text weight="semibold">4–5 · højere udtryk</Text>
                <Text type="supporting" color="secondary">Trækket er mere gennemgående på tværs af situationer.</Text>
              </VStack>
            </VStack>
          </VStack>
        </Section>
      </Grid>
      <VStack gap={4}>
        <VStack gap={1}>
          <Heading level={2}>Domæner i kontekst</Heading>
          <Text color="secondary">Læs begge ender af hvert spektrum. Ingen af dem er automatisk den rigtige.</Text>
        </VStack>
        <Grid columns={{ minWidth: 320, max: 2 }} gap={3}>
        {domains.map((score: any) => (
          <Section key={score.key} variant="muted" padding={4}>
            <VStack gap={3}>
              <HStack hAlign="between" vAlign="start" gap={3}>
                <Heading level={3}>{score.name}</Heading>
                <Text type="large" weight="semibold" className="metric-number">{formatScore(score.value)}{scoreScale.suffix}</Text>
              </HStack>
              <ProgressBar label={score.name} value={score.value} max={scoreScale.maximum} isLabelHidden variant="accent" />
              <Text type="supporting">{score.description}</Text>
              <Grid columns={{ minWidth: 180, max: 2 }} gap={3}>
                <VStack gap={0.5}>
                  <Text type="supporting" weight="semibold">{isCognitive ? "Lavere præstation i kortformen" : "Lavere udtryk"}</Text>
                  <Text type="supporting" color="secondary">{score.lowExpression}</Text>
                </VStack>
                <VStack gap={0.5}>
                  <Text type="supporting" weight="semibold">{isCognitive ? "Højere præstation i kortformen" : "Højere udtryk"}</Text>
                  <Text type="supporting" color="secondary">{score.highExpression}</Text>
                </VStack>
              </Grid>
            </VStack>
          </Section>
        ))}
        </Grid>
      </VStack>
      {facets.length > 0 && <VStack gap={3}>
        <VStack gap={1}>
          <Heading level={2}>{isCognitive ? "Supplerende mål" : `${facets.length} facetter`}</Heading>
          <Text color="secondary">{isCognitive ? "Viser rå præstation ved siden af det sværhedsjusterede indeks." : "De mere præcise byggesten bag domænescorerne."}</Text>
        </VStack>
        <Grid columns={{ minWidth: 300, max: 2 }} gap={2}>
          {facets.map((score: any) => (
            <Section key={score.key} variant="muted" padding={4}>
              <VStack gap={2}>
                <HStack hAlign="between" gap={3}>
                  <Heading level={4}>{score.name}</Heading>
                  <Text weight="semibold" className="metric-number">{formatScore(score.value)}{scoreScale.suffix}</Text>
                </HStack>
                <ProgressBar label={score.name} value={score.value} max={scoreScale.maximum} isLabelHidden variant="accent" />
                <Text type="supporting" color="secondary">{score.description}</Text>
              </VStack>
            </Section>
          ))}
        </Grid>
      </VStack>}
      <Section variant="muted" padding={5}>
        <VStack gap={2}>
          <Heading level={2}>Eksportens målekontekst</Heading>
          <Text>{result.measurement.purpose}</Text>
          <Text type="supporting">{result.measurement.scoring}</Text>
          {result.measurement.limitations.map((limit: string) => <Text key={limit} type="supporting">• {limit}</Text>)}
        </VStack>
      </Section>
    </VStack>
  );
}

const scoreLabelsPlugin = {
  id: "psychometrics-score-labels",
  afterDatasetsDraw(chart: any) {
    const { ctx } = chart;
    ctx.save();
    ctx.fillStyle = chart.options.plugins.psychometricsScoreLabels.color;
    ctx.font = `600 ${chart.options.plugins.psychometricsScoreLabels.fontSize} ${chart.options.plugins.psychometricsScoreLabels.fontFamily}`;
    ctx.textBaseline = "middle";
    chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((bar: any, index: number) => {
        const value = Number(dataset.data[index]);
        ctx.fillText(`${formatScore(value)}${chart.options.plugins.psychometricsScoreLabels.suffix}`, bar.x + 8, bar.y);
      });
    });
    ctx.restore();
  },
};

function DomainChart({ scores, minimum = 1, maximum = 5, suffix = "/5" }: { scores: any[]; minimum?: number; maximum?: number; suffix?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { token } = useTheme();
  const chartTheme = useMemo(() => ({
    accent: token("--color-accent"),
    surface: token("--color-background-surface"),
    text: token("--color-text-primary"),
    secondary: token("--color-text-secondary"),
    border: token("--color-border"),
    font: token("--font-family-body"),
    fontSize: token("--font-size-sm"),
  }), [token]);
  useEffect(() => {
    if (!ref.current) return;
    const chart = new Chart(ref.current, {
      type: "bar",
      data: {
        labels: scores.map(score => chartLabel(score.name)),
        datasets: [{
          label: "Score",
          data: scores.map(score => Number(score.value)),
          backgroundColor: chartTheme.accent,
          borderColor: chartTheme.accent,
          borderWidth: 1,
          borderRadius: 8,
          borderSkipped: false,
          barPercentage: 0.68,
          categoryPercentage: 0.72,
          maxBarThickness: 34,
        }]
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 650, easing: "easeOutQuart" },
        layout: { padding: { right: 46 } },
        scales: {
          x: {
            min: minimum,
            max: maximum,
            border: { display: false },
            grid: { color: chartTheme.border },
            ticks: { stepSize: maximum <= 5 ? 1 : 20, color: chartTheme.secondary, font: { family: chartTheme.font } },
          },
          y: {
            border: { display: false },
            grid: { display: false },
            ticks: { color: chartTheme.text, font: { family: chartTheme.font, weight: 600 } },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: chartTheme.text,
            titleColor: chartTheme.surface,
            bodyColor: chartTheme.surface,
            padding: 12,
            displayColors: false,
            callbacks: { label: (context: any) => `${context.raw}${suffix}` },
          },
          psychometricsScoreLabels: { color: chartTheme.text, fontFamily: chartTheme.font, fontSize: chartTheme.fontSize, suffix },
        } as any,
      },
      plugins: [scoreLabelsPlugin],
    });
    return () => chart.destroy();
  }, [scores, chartTheme, minimum, maximum, suffix]);
  return (
    <Section variant="muted" padding={3} className={scores.length > 10 ? "chart-frame chart-frame-long" : "chart-frame"}>
      <canvas ref={ref} className="psychometrics-chart" aria-label={`Domænescorer på en skala fra ${minimum} til ${maximum}`} role="img" />
    </Section>
  );
}

function ComparisonChart({ leftName, rightName, left, right }: { leftName: string; rightName: string; left: any[]; right: any[] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { token } = useTheme();
  const chartTheme = useMemo(() => ({
    accent: token("--color-accent"),
    comparison: token("--color-border-green"),
    surface: token("--color-background-surface"),
    text: token("--color-text-primary"),
    secondary: token("--color-text-secondary"),
    border: token("--color-border"),
    font: token("--font-family-body"),
    fontSize: token("--font-size-sm"),
  }), [token]);
  useEffect(() => {
    if (!ref.current) return;
    const chart = new Chart(ref.current, {
      type: "bar",
      data: {
        labels: left.map(score => chartLabel(score.name)),
        datasets: [
          {
            label: leftName,
            data: left.map(score => Number(score.value)),
            backgroundColor: chartTheme.accent,
            borderRadius: 7,
            borderSkipped: false,
            barPercentage: 0.72,
            categoryPercentage: 0.72,
            maxBarThickness: 24,
          },
          {
            label: rightName,
            data: left.map(score => Number(right.find(candidate => candidate.key === score.key)?.value)),
            backgroundColor: chartTheme.comparison,
            borderRadius: 7,
            borderSkipped: false,
            barPercentage: 0.72,
            categoryPercentage: 0.72,
            maxBarThickness: 24,
          }
        ]
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 650, easing: "easeOutQuart" },
        scales: {
          x: {
            min: 1,
            max: 5,
            border: { display: false },
            grid: { color: chartTheme.border },
            ticks: { stepSize: 1, color: chartTheme.secondary, font: { family: chartTheme.font } },
          },
          y: {
            border: { display: false },
            grid: { display: false },
            ticks: { color: chartTheme.text, font: { family: chartTheme.font, weight: 600 } },
          },
        },
        plugins: {
          legend: {
            position: "bottom",
            align: "start",
            labels: { color: chartTheme.text, boxWidth: 10, boxHeight: 10, padding: 18, font: { family: chartTheme.font } },
          },
          tooltip: {
            backgroundColor: chartTheme.text,
            titleColor: chartTheme.surface,
            bodyColor: chartTheme.surface,
            padding: 12,
            displayColors: true,
            callbacks: { label: (context: any) => `${context.dataset.label}: ${context.raw}/5` },
          },
        },
      }
    });
    return () => chart.destroy();
  }, [leftName, rightName, left, right, chartTheme]);
  return (
    <Section variant="muted" padding={3} className="chart-frame comparison-chart-frame">
      <canvas ref={ref} className="psychometrics-chart" aria-label="Sammenligning af domænescorer på en skala fra 1 til 5" role="img" />
    </Section>
  );
}

function Admin() {
  const [users, setUsers] = useState<User[]>([]);
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [notice, setNotice] = useState<{ type: "error" | "success"; text: string } | null>(null);

  const load = () => api<{ users: User[] }>("/api/admin/users").then(data => setUsers(data.users));
  useEffect(() => { load().catch(error => setNotice({ type: "error", text: message(error) })); }, []);

  return (
    <VStack gap={6} maxWidth={1000} className="page-frame">
      <VStack gap={1}><Heading level={1}>Brugere</Heading><Text color="secondary">Opret brugere og sæt midlertidige adgangskoder.</Text></VStack>
      {notice && <Banner status={notice.type} title={notice.type === "success" ? "Gemt" : "Fejl"} description={notice.text} isDismissable onDismiss={() => setNotice(null)} />}
      <Section variant="muted" padding={5}>
        <VStack gap={4}>
          <Heading level={2}>Tilføj bruger</Heading>
          <Grid columns={{ minWidth: 220, max: 2 }} gap={3}>
            <TextInput label="Navn" value={displayName} onChange={setDisplayName} aria-required="true" />
            <TextInput label="Brugernavn" value={username} onChange={setUsername} aria-required="true" />
            <TextInput label="Midlertidig adgangskode" type="password" value={password} onChange={setPassword} description="Mindst 12 tegn; brugeren skal skifte den ved første login." aria-required="true" />
            <Selector label="Rolle" options={[{ value: "user", label: "Bruger" }, { value: "admin", label: "Administrator" }]} value={role} onChange={setRole} />
          </Grid>
          <Button label="Opret bruger" variant="primary" clickAction={async () => {
            setNotice(null);
            try {
              await api("/api/admin/users", { method: "POST", body: JSON.stringify({ username, displayName, password, role }) });
              setUsername(""); setDisplayName(""); setPassword(""); setRole("user");
              await load();
              setNotice({ type: "success", text: "Brugeren er oprettet." });
            } catch (error) {
              setNotice({ type: "error", text: message(error) });
            }
          }} />
        </VStack>
      </Section>
      <Grid columns={{ minWidth: 260, max: 3 }} gap={3}>
        {users.map(user => <UserCard key={user.id} user={user} onNotice={setNotice} onSaved={load} />)}
      </Grid>
    </VStack>
  );
}

function UserCard({ user, onNotice, onSaved }: { user: User; onNotice: (notice: { type: "error" | "success"; text: string }) => void; onSaved: () => Promise<void> }) {
  const [password, setPassword] = useState("");
  return (
    <Card>
      <VStack gap={3}>
        <HStack gap={3} vAlign="center">
          <Avatar name={user.display_name} size="lg" />
          <VStack gap={0.5}>
            <Heading level={3}>{user.display_name}</Heading>
            <Text type="supporting">@{user.username} · {user.role === "admin" ? "Administrator" : "Bruger"}</Text>
          </VStack>
        </HStack>
        <Text type="supporting">{user.must_change_password ? "Midlertidig adgangskode" : "Aktiv adgangskode"}</Text>
        <TextInput label="Ny midlertidig adgangskode" type="password" value={password} onChange={setPassword} description="Mindst 12 tegn." />
        <Button label="Sæt adgangskode" variant="secondary" isDisabled={password.length < 12} clickAction={async () => {
          try {
            await api(`/api/admin/users/${user.id}/password`, { method: "POST", body: JSON.stringify({ password }) });
            setPassword(""); await onSaved();
            onNotice({ type: "success", text: `Adgangskoden er nulstillet for ${user.display_name}.` });
          } catch (error) {
            onNotice({ type: "error", text: message(error) });
          }
        }} />
      </VStack>
    </Card>
  );
}

function Account({ user, onChanged }: { user: User; onChanged: () => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [notice, setNotice] = useState<{ type: "error" | "success"; text: string } | null>(null);
  return (
    <VStack gap={5} maxWidth={680} className="page-frame">
      <VStack gap={1}><Heading level={1}>Konto</Heading><Text color="secondary">@{user.username}</Text></VStack>
      {notice && <Banner status={notice.type} title={notice.type === "success" ? "Adgangskode ændret" : "Kunne ikke ændre adgangskoden"} description={notice.text} />}
      <Section variant="muted" padding={5}>
        <VStack gap={4}>
          <Heading level={2}>Skift adgangskode</Heading>
          <TextInput label="Nuværende adgangskode" type="password" value={currentPassword} onChange={setCurrentPassword} aria-required="true" />
          <TextInput label="Ny adgangskode" type="password" value={newPassword} onChange={setNewPassword} description="Mindst 12 tegn." aria-required="true" />
          <Button label="Skift adgangskode" variant="primary" clickAction={async () => {
            try {
              await api("/api/password", { method: "POST", body: JSON.stringify({ currentPassword, newPassword }) });
              setCurrentPassword(""); setNewPassword(""); onChanged();
              setNotice({ type: "success", text: "Din adgangskode er opdateret, og andre sessioner er logget ud." });
            } catch (error) {
              setNotice({ type: "error", text: message(error) });
            }
          }} />
        </VStack>
      </Section>
    </VStack>
  );
}

async function api<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  if (csrfToken && init.method && init.method !== "GET") headers.set("X-CSRF-Token", csrfToken);
  const response = await fetch(path, { ...init, headers });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
  return data as T;
}

function parseRoute(): { name: string; id: number; leftId: number; rightId: number } {
  const path = location.hash.replace(/^#/, "") || "/";
  const test = path.match(/^\/test\/(\d+)$/);
  if (test) return { name: "test", id: Number(test[1]), leftId: 0, rightId: 0 };
  const result = path.match(/^\/result\/(\d+)$/);
  if (result) return { name: "result", id: Number(result[1]), leftId: 0, rightId: 0 };
  const compare = path.match(/^\/compare\/(\d+)\/(\d+)$/);
  if (compare) return { name: "compare", id: 0, leftId: Number(compare[1]), rightId: Number(compare[2]) };
  if (path === "/results") return { name: "results", id: 0, leftId: 0, rightId: 0 };
  if (path === "/admin") return { name: "admin", id: 0, leftId: 0, rightId: 0 };
  if (path === "/account") return { name: "account", id: 0, leftId: 0, rightId: 0 };
  return { name: "dashboard", id: 0, leftId: 0, rightId: 0 };
}

function formatDate(value: string) {
  const normalized = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  return new Intl.DateTimeFormat("da-DK", { dateStyle: "medium", timeStyle: "short" }).format(new Date(normalized));
}

function formatScore(value: number | string) {
  return new Intl.NumberFormat("da-DK", { maximumFractionDigits: 2 }).format(Number(value));
}

function chartLabel(value: string): string | string[] {
  if (value === "Samvittighedsfuldhed") return ["Samvittigheds-", "fuldhed"];
  if (value === "Emotionelle reaktioner") return ["Emotionelle", "reaktioner"];
  return value;
}

function message(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

createRoot(document.getElementById("root")!).render(
  <InternationalizationProvider locale="da" messages={{ da: astryxDanish }}>
    <Theme theme={psychometricsMatchaTheme} mode="light">
      <App />
    </Theme>
  </InternationalizationProvider>
);
